/**
 * Lightweight weekly reading stats for the "Your Week in InSnip" recap. The pure helpers
 * (isoWeekKey, topTopics) are unit tested; the localStorage counter accrues going forward and
 * resets each ISO week. House style: no em dashes.
 */

/** ISO week key like "2026-W26" for the given date (local). */
export function isoWeekKey(d: Date): string {
  const date = new Date(Date.UTC(d.getFullYear(), d.getMonth(), d.getDate()));
  const dayNum = (date.getUTCDay() + 6) % 7; // Monday = 0
  date.setUTCDate(date.getUTCDate() - dayNum + 3); // nearest Thursday
  const firstThursday = new Date(Date.UTC(date.getUTCFullYear(), 0, 4));
  const week =
    1 +
    Math.round(
      (date.getTime() - firstThursday.getTime()) / 86_400_000 / 7,
    );
  return `${date.getUTCFullYear()}-W${String(week).padStart(2, "0")}`;
}

/** The reader's strongest topics, highest first, from the affinity-signal map. */
export function topTopics(signals: Record<string, number> | null | undefined, n = 3): string[] {
  if (!signals) return [];
  return Object.entries(signals)
    .filter(([, v]) => Number(v) > 0)
    .sort((a, b) => Number(b[1]) - Number(a[1]))
    .slice(0, n)
    .map(([k]) => k);
}

interface WeekRead {
  week: string;
  count: number;
  ids: string[];
}

const KEY = "insnip.weekRead";

function load(now: Date): WeekRead {
  const week = isoWeekKey(now);
  try {
    const raw = localStorage.getItem(KEY);
    if (raw) {
      const d = JSON.parse(raw) as WeekRead;
      if (d && d.week === week) return { week, count: d.count || 0, ids: Array.isArray(d.ids) ? d.ids : [] };
    }
  } catch {
    /* ignore */
  }
  return { week, count: 0, ids: [] };
}

/** Count a card as read this week (deduped by id). Returns the new weekly total. */
export function recordWeeklyRead(id: string, now: Date = new Date()): number {
  const data = load(now);
  if (id && !data.ids.includes(id)) {
    data.ids.push(id);
    data.count += 1;
    if (data.ids.length > 500) data.ids = data.ids.slice(-500);
    try {
      localStorage.setItem(KEY, JSON.stringify(data));
    } catch {
      /* ignore */
    }
  }
  return data.count;
}

/** Distinct cards read in the current ISO week. */
export function weeklyReadCount(now: Date = new Date()): number {
  return load(now).count;
}
